/**
 * AI treatment-plan revision flow rendered inside the AI Assistant Drawer.
 *
 * Provider describes the changes they want; the bot returns revision suggestions
 * that must be approved before they update the local draft. Creation / first
 * draft is handled by Quick generate — this flow is revise-only.
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconSend, IconX } from '@tabler/icons-react';
import { useMedplum } from '@medplum/react-hooks';
import { useMemo, useRef, useState } from 'react';

import { ChatMessageBubble } from './ChatMessageBubble';
import { useScrollLatestChatToTop } from './useScrollLatestChatToTop';
import { requestAiTreatmentPlanRevision } from '../../lib/aiTreatmentPlanRevisionService';
import {
  formatTreatmentPlanRevisionOperation,
  type TreatmentPlanRevisionSuggestion,
} from '../../lib/treatmentPlanAiRevision';
import { ChatChecklistCard } from './chat-artifacts/ChatChecklistCard';
import type { ChatChecklistPart } from './chat-artifacts/chatMessageParts';
import type { TreatmentPlanRevisionContext } from './types';
import {
  composeTreatmentPlanRevisionInstruction,
  TREATMENT_PLAN_REVISION_QUICK_PROMPTS,
} from './treatmentPlanQuickPrompts';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'error';
  text: string;
}

export const TREATMENT_PLAN_REVISE_INTRO_MESSAGE =
  'What would you like to change? Share the revisions you want — I will suggest edits for you to '
  + 'approve before they are applied to the draft.';

let messageIdCounter = 0;
function nextMessageId(role: string): string {
  messageIdCounter += 1;
  return `${role}-${messageIdCounter}`;
}

export interface TreatmentPlanRevisionFlowProps {
  context: TreatmentPlanRevisionContext;
}

export function TreatmentPlanRevisionFlow({ context }: TreatmentPlanRevisionFlowProps): JSX.Element {
  const medplum = useMedplum();
  const {
    patientId,
    episodeId,
    questionnaire,
    getSavedResponse,
    getLiveResponse,
    onApplySuggestions,
    disabled = false,
    geminiAiDisabled = false,
  } = context;

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: nextMessageId('assistant'),
      role: 'assistant',
      text: TREATMENT_PLAN_REVISE_INTRO_MESSAGE,
    },
  ]);
  const [input, setInput] = useState('');
  const [checklistKey, setChecklistKey] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestion, setSuggestion] = useState<TreatmentPlanRevisionSuggestion | null>(null);
  const inFlightRef = useRef(false);
  const latestMessageKey = messages.length > 0 ? messages[messages.length - 1].id : undefined;
  const { viewportRef, latestMessageRef } = useScrollLatestChatToTop(
    messages.length,
    latestMessageKey,
  );

  const revisionChecklist: ChatChecklistPart = useMemo(
    () => ({
      type: 'checklist',
      id: `revision-quick-${checklistKey}`,
      prompt: 'What would you like to revise?',
      selectionMode: 'multiple',
      options: TREATMENT_PLAN_REVISION_QUICK_PROMPTS.map((option) => ({
        id: option.id,
        label: option.label,
        description: option.prompt,
      })),
      allowOther: true,
      otherLabel: 'Additional instructions',
      submitLabel: 'Send revision request',
    }),
    [checklistKey],
  );

  const outgoingText = input.trim();
  const canSend = !disabled && !geminiAiDisabled && !loading && outgoingText.length > 0;

  const appendMessage = (role: ChatMessage['role'], text: string): void => {
    setMessages((prev) => [...prev, { id: nextMessageId(role), role, text }]);
  };

  /** Sends the CURRENT live draft as context on every request. */
  const runRevise = async (prompt: string): Promise<void> => {
    const savedResponse = getSavedResponse();
    const liveResponse = getLiveResponse();
    const draftResponse = liveResponse ?? savedResponse ?? undefined;

    try {
      const request = {
        patientId,
        episodeId,
        prompt,
        questionnaire,
        questionnaireResponse: {
          resourceType: 'QuestionnaireResponse' as const,
          status: 'in-progress' as const,
          questionnaire: questionnaire.url
            ? `${questionnaire.url}|${questionnaire.version ?? '1.0.0'}`
            : `Questionnaire/${questionnaire.id}`,
          subject: { reference: `Patient/${patientId}` },
          ...(savedResponse?.id ? { id: savedResponse.id } : {}),
          item: draftResponse?.item ?? [],
        },
      };

      const result = await requestAiTreatmentPlanRevision(medplum, request);
      setSuggestion(result);
      appendMessage('assistant', result.summary);
    } catch (err) {
      const message = getUserFacingErrorMessage(err, 'Something went wrong. Please try again.');
      setError(message);
      appendMessage('error', message);
    }
  };

  const handleSend = async (): Promise<void> => {
    if (!canSend || inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    setSuggestion(null);

    const text = outgoingText;
    setMessages((prev) => [...prev, { id: nextMessageId('user'), role: 'user', text }]);
    setInput('');
    setChecklistKey((key) => key + 1);

    try {
      await runRevise(text);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  const handleApply = () => {
    if (!suggestion) return;
    onApplySuggestions(suggestion);
    setSuggestion(null);
    appendMessage('assistant', 'Applied to the draft. Review the form and save when you are ready.');
  };

  const handleDismiss = () => {
    setSuggestion(null);
    setError(null);
  };

  const operationPreview = useMemo(
    () =>
      suggestion?.operations.map((op, index) => (
        <Box
          key={`${op.linkId}-${index}`}
          p="xs"
          style={{ borderRadius: 8, background: 'var(--mantine-color-gray-0)' }}
        >
          <Text size="xs" fw={600}>
            {formatTreatmentPlanRevisionOperation(op, questionnaire)}
          </Text>
          {op.valueString && (
            <Text size="xs" c="dimmed" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
              {op.valueString}
            </Text>
          )}
          {op.initialAnswers?.map((answer) => (
            <Text key={answer.linkId} size="xs" c="dimmed" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
              {answer.linkId}: {answer.valueString}
            </Text>
          ))}
          {op.rationale && (
            <Text size="xs" c="gray.6" mt={4} fs="italic">
              {op.rationale}
            </Text>
          )}
        </Box>
      )),
    [questionnaire, suggestion?.operations],
  );

  if (geminiAiDisabled) {
    return (
      <Stack gap="sm" p="md">
        <Badge size="xs" variant="light" color="gray" w="fit-content">
          Disabled
        </Badge>
        <Text size="sm" c="gray.7">
          AI documentation assistance is disabled for this provider or client. You can still edit the
          treatment plan manually.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap={0} h="100%" style={{ minHeight: 0, overflow: 'hidden' }}>
      <ScrollArea type="auto" viewportRef={viewportRef} style={{ flex: 1, minHeight: 0 }}>
        <Stack gap="md" p="md" role="log" aria-label="Treatment plan assistant conversation">
          {disabled && (
            <Badge size="xs" variant="light" color="gray" w="fit-content">
              Read-only
            </Badge>
          )}
          {messages.map((msg, index) => (
            <ChatMessageBubble
              key={msg.id}
              ref={index === messages.length - 1 ? latestMessageRef : undefined}
              role={msg.role}
              text={msg.text}
            />
          ))}

          {loading && (
            <Group gap="xs" p="xs">
              <Loader size="xs" />
              <Text size="xs" c="dimmed">
                Preparing revision suggestions…
              </Text>
            </Group>
          )}

          {suggestion && (
            <Box
              p="sm"
              style={{
                borderRadius: 10,
                border: '1px solid var(--mantine-color-gold-3)',
                background: 'var(--mantine-color-white)',
              }}
            >
              <Stack gap="xs">
                <Text size="sm" fw={600}>
                  Suggested changes
                </Text>
                {suggestion.warnings?.map((warning) => (
                  <Alert key={warning} color="yellow" variant="light" p="xs">
                    {warning}
                  </Alert>
                ))}
                <Stack gap="xs">{operationPreview}</Stack>
                <Group gap="xs">
                  <Button
                    size="compact-sm"
                    color="gold"
                    leftSection={<IconCheck size={14} />}
                    onClick={handleApply}
                  >
                    Apply suggestions
                  </Button>
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconX size={14} />}
                    onClick={handleDismiss}
                  >
                    Dismiss
                  </Button>
                </Group>
              </Stack>
            </Box>
          )}

          {!loading && !suggestion && (
            <ChatChecklistCard
              key={revisionChecklist.id}
              checklist={revisionChecklist}
              disabled={disabled}
              onSubmit={(submission) => {
                if (disabled || loading || submission.skipped) return;
                const composed = composeTreatmentPlanRevisionInstruction(
                  submission.selectedOptionIds,
                  submission.otherText ?? '',
                );
                if (!composed.trim()) return;
                setInput(composed);
                queueMicrotask(() => {
                  void (async () => {
                    if (inFlightRef.current) return;
                    inFlightRef.current = true;
                    setLoading(true);
                    setError(null);
                    setSuggestion(null);
                    const text = composed.trim();
                    setMessages((prev) => [
                      ...prev,
                      { id: nextMessageId('user'), role: 'user', text },
                    ]);
                    setInput('');
                    setChecklistKey((key) => key + 1);
                    try {
                      await runRevise(text);
                    } finally {
                      inFlightRef.current = false;
                      setLoading(false);
                    }
                  })();
                });
              }}
            />
          )}
        </Stack>
      </ScrollArea>

      <Box
        p="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          background: 'var(--mantine-color-white)',
          flexShrink: 0,
        }}
      >
        <Stack gap="xs">
          {error && !suggestion && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" p="xs">
              {error}
            </Alert>
          )}

          <Text size="xs" c="gray.7">
            Suggestions update your local draft only. Provider review is required — use Save and
            Sign to finalize.
          </Text>

          <Group gap="xs" align="flex-end" wrap="nowrap">
            <Textarea
              aria-label="Revision request"
              placeholder="Describe the changes you want… (Enter to send)"
              value={input}
              onChange={(e) => setInput(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (canSend) void handleSend();
                }
              }}
              autosize
              minRows={2}
              maxRows={4}
              disabled={disabled || loading}
              style={{ flex: 1 }}
              styles={{ input: { borderRadius: 10 } }}
            />
            <ActionIcon
              size="lg"
              color="gold"
              variant="filled"
              onClick={() => void handleSend()}
              disabled={!canSend}
              loading={loading}
              aria-label="Send revision request"
            >
              <IconSend size={16} />
            </ActionIcon>
          </Group>
        </Stack>
      </Box>
    </Stack>
  );
}
